import { FC, ReactNode } from "react";
import { NavLink, useLocation } from "react-router";

import classNames from "classnames";

import { BaseProps } from "../../BaseProps";
import { getHtmlPropsFromBaseProps } from "../../utilities/getHtmlPropsFromBaseProps";
import { Tooltip } from "../Tooltip";

import legacyStyles from "./legacy/styles.module.scss";
import tokenStyles from "./styles.module.css";
import { createComponentStyleHook } from "../../tokens";

/** The `Item` properties. */
export interface ItemProps extends BaseProps {
  /** The path to navigate to. */
  readonly path?: string;

  /** The optional switch to disable exact path matching. */
  readonly nonExactMatch?: boolean;

  /** The item tooltip. */
  readonly tooltip?: ReactNode;

  /** The component children. */
  readonly children?: ReactNode;
}

const useStyles = createComponentStyleHook({
  legacyStyles,
  tokenStyles,
  componentTokens: "sidebar_nav",
});

/** The in-context sidebar navigation component. */
export const Item: FC<ItemProps> = (props) => {
  const { children, path, nonExactMatch = false, tooltip } = props;
  const { mode, styles, StyleModeBoundary } = useStyles(props);

  const location = useLocation();

  const baseHtmlProps = getHtmlPropsFromBaseProps(props, {
    className: [styles.Item, "item"],
  });

  if (path) {
    let isSubNavigation = false;
    let isSubActive = false;

    if (path.startsWith("#")) {
      isSubNavigation = true;
      const url = new URL(`http://test/${path}`);
      isSubActive = url.hash === location.hash;
    }

    const hashAriaCurrent = isSubNavigation && isSubActive ? "page" : undefined;

    return (
      <StyleModeBoundary mode={mode}>
        <Tooltip content={tooltip}>
          {(ref) => (
            <NavLink
              {...baseHtmlProps}
              ref={ref}
              to={path}
              end={!nonExactMatch}
              className={({ isActive: navIsActive }) =>
                classNames(baseHtmlProps.className, { active: !isSubNavigation ? navIsActive : navIsActive && isSubActive })
              }
              aria-current={isSubNavigation ? hashAriaCurrent : undefined}
            >
              {children}
            </NavLink>
          )}
        </Tooltip>
      </StyleModeBoundary>
    );
  }

  return (
    <Tooltip content={tooltip}>
      {(ref) => (
        <div {...baseHtmlProps} ref={ref}>
          {children}
        </div>
      )}
    </Tooltip>
  );
};
